import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Download, ArrowRight, Code2, Briefcase, Cpu } from 'lucide-react';
import { assets } from '../../assets/assets';
import { MagneticButton } from './MagneticButton';
import { scrollToSection } from '../utils/scroll';
import { Lanyard } from './Lanyard';
import { createLanyardTextures } from '../utils/lanyardTextures';
import { CardFullscreenModal } from './CardFullscreenModal';

const professionalRoles = [
  'Full-Stack Software Engineer',
  'Spring Boot & React Specialist',
  'Cloud & Architecture Enthusiast',
  'Creative Problem Solver',
];

export const Hero: React.FC = () => {
  const [isFullscreenModalOpen, setIsFullscreenModalOpen] = useState(false);
  const [currentRoleText, setCurrentRoleText] = useState('');
  const [roleIndex, setRoleIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const [cardTextures, setCardTextures] = useState<{ front: string | null; back: string | null }>({
    front: null,
    back: null,
  });

  const scrollToAbout = () => {
    scrollToSection('about');
  };

  const scrollToProjects = () => {
    scrollToSection('projects');
  };

  // Load lanyard textures asynchronously
  useEffect(() => {
    const { front, back } = createLanyardTextures();
    Promise.all([front, back]).then(([frontImg, backImg]) => {
      setCardTextures({ front: frontImg, back: backImg });
    });
  }, []);

  // Smooth, jitter-free typing animation
  useEffect(() => {
    const currentRole = professionalRoles[roleIndex];
    let timeout: NodeJS.Timeout;

    if (!isDeleting) {
      if (currentRoleText.length < currentRole.length) {
        timeout = setTimeout(() => {
          setCurrentRoleText(currentRole.slice(0, currentRoleText.length + 1));
        }, 55);
      } else {
        timeout = setTimeout(() => {
          setIsDeleting(true);
        }, 2200);
      }
    } else {
      if (currentRoleText.length > 0) {
        timeout = setTimeout(() => {
          setCurrentRoleText(currentRole.slice(0, currentRoleText.length - 1));
        }, 28);
      } else {
        setIsDeleting(false);
        setRoleIndex((prev) => (prev + 1) % professionalRoles.length);
      }
    }

    return () => clearTimeout(timeout);
  }, [currentRoleText, isDeleting, roleIndex]);

  // Framer Motion staggered animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section
      id="hero"
      className="min-h-[calc(100svh-4rem)] sm:min-h-[calc(100svh-5rem)] lg:h-[calc(100svh-5rem)] flex flex-col justify-center py-6 sm:py-10 lg:py-0 relative overflow-hidden"
    >
      {/* Dynamic Background Subtle Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none" aria-hidden="true">
        <svg width="100%" height="100%" className="absolute inset-0">
          <defs>
            <pattern id="hero-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-grid)" className="text-brand-purple" />
        </svg>
      </div>

      {/* Atmospheric Soft Light Aura with gentle ambient breathing */}
      <motion.div
        animate={{
          scale: [1, 1.08, 1],
          opacity: [0.15, 0.22, 0.15],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        className="absolute -top-24 left-1/4 w-60 xs:w-72 sm:w-96 h-60 xs:h-72 sm:h-96 bg-brand-purple/20 dark:bg-brand-purple/25 rounded-full blur-[90px] sm:blur-[140px] pointer-events-none"
      />
      <motion.div
        animate={{
          scale: [1.05, 1, 1.05],
          opacity: [0.12, 0.18, 0.12],
        }}
        transition={{
          duration: 9,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        className="absolute top-1/3 right-2 sm:right-10 w-60 xs:w-72 sm:w-96 h-60 xs:h-72 sm:h-96 bg-brand-blue/15 dark:bg-brand-blue/20 rounded-full blur-[90px] sm:blur-[140px] pointer-events-none"
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 z-10 max-w-7xl h-full flex flex-col justify-center">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-8 xl:gap-12 items-center">
          {/* Left Column (Hero Content) with Framer Motion Stagger */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="lg:col-span-7 text-left flex flex-col justify-center order-1"
          >

            {/* Main Headline */}
            <motion.h1
              variants={itemVariants}
              className="text-2xl xs:text-3xl sm:text-5xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight leading-[1.15] xs:leading-[1.12] mb-3 sm:mb-4"
            >
              <span className="block text-brand-navy dark:text-white font-inter">Hi, I'm</span>
              <span className="bg-gradient-to-r from-brand-purple via-brand-blue to-brand-light bg-clip-text text-transparent font-inter">
                Nugitha Disas
              </span>
            </motion.h1>

            {/* Dynamic Role Typewriter */}
            <motion.div
              variants={itemVariants}
              className="text-sm xs:text-base sm:text-xl lg:text-2xl font-medium text-brand-navy/80 dark:text-brand-dark-text-secondary mb-3 sm:mb-5 min-h-[2.75rem] xs:min-h-[2.5rem] sm:min-h-[2.25rem] flex items-baseline flex-wrap gap-y-1 w-full max-w-full"
            >
              <span className="text-brand-navy/70 dark:text-brand-dark-text-muted shrink-0">I craft scalable systems as a&nbsp;</span>
              <span className="font-bold text-brand-purple dark:text-brand-light inline-flex items-center">
                <span>{currentRoleText}</span>
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.6, repeat: Infinity, ease: 'easeInOut' }}
                  className="inline-block w-[2px] h-[1.1em] bg-brand-purple dark:bg-brand-light ml-1 rounded-full shadow-sm"
                />
              </span>
            </motion.div>

            {/* Concise Bio Description */}
            <motion.p
              variants={itemVariants}
              className="text-xs xs:text-sm sm:text-base lg:text-lg text-brand-navy/75 dark:text-brand-dark-text-muted mb-5 sm:mb-7 max-w-2xl leading-relaxed font-normal"
            >
              Undergraduate software engineer dedicated to building resilient, high-performance web applications and backend systems. Combining deep expertise in Java, Spring Boot, React, and TypeScript to architect elegant digital solutions.
            </motion.p>

            {/* CTA Action Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap gap-2.5 sm:gap-4 items-center mb-5 sm:mb-8 w-full"
            >
              <MagneticButton className="flex-1 xs:flex-none">
                <button
                  onClick={scrollToProjects}
                  className="w-full xs:w-auto justify-center px-4 xs:px-5 sm:px-7 py-2.5 xs:py-3 sm:py-3.5 rounded-2xl text-white text-xs xs:text-sm sm:text-base font-semibold bg-gradient-to-r from-brand-blue via-brand-purple to-brand-blue bg-size-200 hover:bg-right shadow-lg shadow-brand-purple/25 hover:shadow-brand-purple/40 transition-all duration-300 flex items-center gap-2 group cursor-pointer"
                  data-cursor="button"
                >
                  <span className="truncate">Explore Work</span>
                  <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform shrink-0" />
                </button>
              </MagneticButton>

              <MagneticButton className="flex-1 xs:flex-none">
                <a
                  href={assets.cv_pdf}
                  download="Nugitha-Disas-CV.pdf"
                  className="w-full xs:w-auto justify-center px-4 xs:px-5 sm:px-7 py-2.5 xs:py-3 sm:py-3.5 rounded-2xl text-xs xs:text-sm sm:text-base font-semibold border border-brand-purple/40 dark:border-brand-purple/50 text-brand-navy dark:text-brand-dark-text bg-white/60 dark:bg-brand-dark-bg/60 hover:bg-brand-purple hover:text-white dark:hover:bg-brand-purple transition-all duration-300 backdrop-blur-sm flex items-center gap-2 shadow-sm cursor-pointer"
                  data-cursor="button"
                >
                  <Download size={17} className="shrink-0" />
                  <span className="truncate">Download CV</span>
                </a>
              </MagneticButton>
            </motion.div>

            {/* Key Quick Metrics Grid with Stagger and Hover Micro-Animations */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-3 gap-1.5 xs:gap-3 sm:gap-4 w-full max-w-lg pt-3 sm:pt-5 border-t border-brand-light/30 dark:border-brand-purple/25"
            >
              <div className="p-2 xs:p-2.5 sm:p-3.5 rounded-2xl bg-white/60 dark:bg-brand-dark-bg-secondary/50 border border-brand-light/40 dark:border-brand-purple/20 backdrop-blur-sm text-center xs:text-left min-w-0 hover:scale-105 hover:border-brand-purple/50 hover:shadow-md hover:shadow-brand-purple/15 transition-all duration-300">
                <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 justify-center xs:justify-start">
                  <Briefcase size={14} className="text-brand-purple shrink-0 hidden xs:block" />
                  <div className="text-sm xs:text-xl sm:text-2xl lg:text-3xl font-extrabold text-brand-purple dark:text-brand-light">
                    2+
                  </div>
                </div>
                <div className="text-[9px] xs:text-[11px] sm:text-xs text-brand-navy/70 dark:text-brand-dark-text-muted font-medium leading-tight truncate xs:whitespace-normal">
                  Years Experience
                </div>
              </div>

              <div className="p-2 xs:p-2.5 sm:p-3.5 rounded-2xl bg-white/60 dark:bg-brand-dark-bg-secondary/50 border border-brand-light/40 dark:border-brand-purple/20 backdrop-blur-sm text-center xs:text-left min-w-0 hover:scale-105 hover:border-brand-blue/50 hover:shadow-md hover:shadow-brand-blue/15 transition-all duration-300">
                <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 justify-center xs:justify-start">
                  <Code2 size={14} className="text-brand-blue shrink-0 hidden xs:block" />
                  <div className="text-sm xs:text-xl sm:text-2xl lg:text-3xl font-extrabold text-brand-blue dark:text-brand-light">
                    9+
                  </div>
                </div>
                <div className="text-[9px] xs:text-[11px] sm:text-xs text-brand-navy/70 dark:text-brand-dark-text-muted font-medium leading-tight truncate xs:whitespace-normal">
                  Built Projects
                </div>
              </div>

              <div className="p-2 xs:p-2.5 sm:p-3.5 rounded-2xl bg-white/60 dark:bg-brand-dark-bg-secondary/50 border border-brand-light/40 dark:border-brand-purple/20 backdrop-blur-sm text-center xs:text-left min-w-0 hover:scale-105 hover:border-brand-purple/50 hover:shadow-md hover:shadow-brand-purple/15 transition-all duration-300">
                <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 justify-center xs:justify-start">
                  <Cpu size={14} className="text-brand-purple shrink-0 hidden xs:block" />
                  <div className="text-sm xs:text-xl sm:text-2xl lg:text-3xl font-extrabold text-brand-purple dark:text-brand-light">
                    15+
                  </div>
                </div>
                <div className="text-[9px] xs:text-[11px] sm:text-xs text-brand-navy/70 dark:text-brand-dark-text-muted font-medium leading-tight truncate xs:whitespace-normal">
                  Core Tech
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Desktop Right Column Grid Placeholder to preserve layout spacing */}
          <div className="hidden lg:block lg:col-span-5 pointer-events-none order-2" aria-hidden="true" />
        </div>
      </div>

      {/* Big Interactive 3D Physics Lanyard Canvas spanning full height on right side for desktop, stacked gracefully on mobile & tablets */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="lg:absolute lg:top-0 lg:right-0 lg:w-[48vw] xl:w-[46vw] lg:h-full w-full max-w-sm xs:max-w-md sm:max-w-lg lg:max-w-none h-[350px] xs:h-[400px] sm:h-[460px] md:h-[500px] lg:h-full flex flex-col justify-start items-center relative z-40 pointer-events-auto mx-auto mt-2 xs:mt-4 sm:mt-6 lg:mt-0"
      >
        {/* Ambient Radial Glow */}
        <div className="absolute top-1/4 inset-x-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
          <div className="w-56 xs:w-72 sm:w-80 h-56 xs:h-72 sm:h-80 sm:w-[420px] sm:h-[420px] rounded-full bg-gradient-to-tr from-brand-purple/25 to-brand-blue/20 blur-[80px] xs:blur-[100px] sm:blur-[120px]" />
        </div>

        {/* Interactive 3D Lanyard Card Hanging Prominently */}
        <div
          className="w-full h-full relative z-20 flex items-start justify-center cursor-pointer touch-none select-none"
          onDoubleClick={() => setIsFullscreenModalOpen(true)}
        >
          <Lanyard
            position={[0, 0, 11.5]}
            gravity={[0, -40, 0]}
            fov={20}
            frontImage={cardTextures.front}
            backImage={cardTextures.back}
            imageFit="cover"
            lanyardWidth={0.32}
            onCardDoubleClick={() => setIsFullscreenModalOpen(true)}
            className="w-full h-full cursor-pointer"
          />
        </div>
      </motion.div>

      {/* Fullscreen 3D Card Modal */}
      <CardFullscreenModal
        isOpen={isFullscreenModalOpen}
        onClose={() => setIsFullscreenModalOpen(false)}
        frontImage={cardTextures.front}
        backImage={cardTextures.back}
      />

      {/* Scroll Down Indicator with Floating Animation */}
      <motion.button
        initial={{ opacity: 0, y: -10 }}
        animate={{
          opacity: [0.6, 1, 0.6],
          y: [0, 6, 0],
        }}
        transition={{
          duration: 2.2,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.8,
        }}
        type="button"
        onClick={scrollToAbout}
        aria-label="Scroll down to About section"
        className="hidden lg:flex absolute bottom-3 left-1/2 -translate-x-1/2 flex-col items-center gap-1 text-brand-purple/80 dark:text-brand-light/80 hover:text-brand-purple dark:hover:text-white transition-colors cursor-pointer group"
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider opacity-70 group-hover:opacity-100 transition-opacity">
          Explore
        </span>
        <ChevronDown size={18} className="group-hover:translate-y-0.5 transition-transform" />
      </motion.button>
    </section>
  );
};

export default Hero;